"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { signIn } from "next-auth/react";
import { toast } from "sonner";
import { Shield, CalendarDays, ClipboardList, UserCheck } from "lucide-react";
import Image from "next/image";

export default function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const result = await signIn("credentials", {
        redirect: false,
        email,
        password,
      });

      if (result?.error) {
        toast.error("Falha no Login", {
          description: "Email ou senha inválidos. Tente novamente.",
        });
      } else {
        toast.success("Login bem-sucedido!");
        router.push("/create-pop/select-city");
      }
    } catch {
      toast.error("Erro", {
        description: "Ocorreu um erro inesperado. Tente novamente mais tarde.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleLogin();
  };

  return (
    <div className="relative min-h-screen">
      {/* Fundo com gradientes sutis e grid pontilhado */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.08),transparent_45%),radial-gradient(ellipse_at_bottom,rgba(16,185,129,0.08),transparent_45%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:22px_22px]" />

      <div className="relative z-10 container mx-auto grid min-h-[85vh] items-center gap-12 px-4 py-8 md:grid-cols-2 md:py-12 lg:min-h-[90vh] lg:py-0">
        {/* Bloco informativo à esquerda */}
        <div className="hidden md:block">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm text-muted-foreground">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Shield size={14} />
            </span>
            POP Documenter
          </div>

          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Sistema de Documentação de POPs
          </h1>
          <p className="mt-3 max-w-xl text-muted-foreground">
            Plataforma para documentação e gestão de POPs técnicos. Mantenha
            padronização, histórico e execução em um só lugar.
          </p>

          <div className="mt-8 grid max-w-xl gap-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 rounded-md bg-secondary p-2 text-secondary-foreground">
                <CalendarDays size={16} />
              </div>
              <div>
                <p className="font-medium">Agenda e Execução</p>
                <p className="text-sm text-muted-foreground">
                  Acompanhe o ciclo de execução das OS e registros de POPs.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1 rounded-md bg-secondary p-2 text-secondary-foreground">
                <ClipboardList size={16} />
              </div>
              <div>
                <p className="font-medium">Padronização</p>
                <p className="text-sm text-muted-foreground">
                  Modelos consistentes para criação e revisão de procedimentos.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1 rounded-md bg-secondary p-2 text-secondary-foreground">
                <UserCheck size={16} />
              </div>
              <div>
                <p className="font-medium">Acesso Seguro</p>
                <p className="text-sm text-muted-foreground">
                  Área restrita para usuários autorizados com controle de
                  permissões.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>
              © {new Date().getFullYear()} — Todos os direitos reservados
            </span>
            <span className="h-3 w-px bg-border" />
            <span>Desenvolvido por</span>
            <Image
              src="https://souuni.com/wp-content/uploads/2024/07/LOGO.webp"
              alt="Uni Internet"
              width={84}
              height={18}
              className="h-4 w-auto"
              unoptimized
              priority
            />
          </div>
        </div>

        {/* Card de login à direita */}
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-md">
          <Card className="relative w-full border bg-card/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-card/80">
            <CardHeader className="space-y-1">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Shield />
              </div>
              <CardTitle className="text-center text-2xl">
                Área Administrativa
              </CardTitle>
              <CardDescription className="text-center">
                Acesso restrito a administradores autorizados
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu.email@provedor.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button
                className="w-full"
                onClick={handleLogin}
                disabled={isLoading}
              >
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  );
}
